// components/portal/useSessionMessages.ts
import { useCallback, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  buildTimelineSessionFeedKey,
  buildTimelineSubjectFeedKey,
  TIMELINE_FEED_LOAD_LIMIT,
} from '@/kernel/useInventory';
import { parseTimelineMessagePayload } from '#lib/leasing/timeline-events';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { ATTACHMENTS, ATTACHMENTS_EVENT_TYPE, describeDeskMove, readDeskMoves } from '#lib/chat/attachments';
import type {
  ActiveEntityFocus,
  ChatTimelineViewMode,
  ChatSessionInventoryEntityState,
  Message,
  TimelineEventDTO,
} from '#types/types';

type TimelineMessageEventSnapshot = Pick<
  TimelineEventDTO,
  | 'id'
  | 'category'
  | 'eventType'
  | 'payload'
  | 'createdAt'
  | 'targetEntityType'
  | 'targetEntityId'
  | 'chatSessionId'
>;

export { TIMELINE_FEED_LOAD_LIMIT };
const EMPTY_TIMELINE_EVENT_IDS: string[] = [];
const EMPTY_TIMELINE_EVENT_SNAPSHOTS: TimelineMessageEventSnapshot[] = [];

const buildTimelineSubjectKey = (
  targetEntityType: string,
  targetEntityId: string
): string => `${targetEntityType}:${targetEntityId}`;

const getMessageTimelineEventId = (message: Message): string =>
  typeof message.timelineEventId === 'string' ? message.timelineEventId.trim() : '';

const getMessageFallbackKey = (message: Message): string =>
  `${message.role}:${String(message.timestamp ?? '')}`;

const toMessageContentString = (content: Message['content']): string => {
  if (Array.isArray(content)) {
    return content.join('\n');
  }
  return typeof content === 'string' ? content : String(content ?? '');
};

const LEGACY_GENERATION_NOISE_PATTERNS: RegExp[] = [
  /^\d+\s+layers?\s+generated$/i,
  /^\[\s*\d+\s*\|\s*fater-[a-z0-9-_.]+\s*\]$/i,
  /^\[\s*\d+\s*\|\s*Result\s+\d{1,2}:\d{2}:\d{2}(?:\s*[AP]M)?\s*\|\s*[a-z0-9-_.]+\s*\]$/i,
];

const isLegacyGenerationNoiseMessage = (message: Message): boolean => {
  const role = normalizeNonEmptyString(message.role)?.toLowerCase();
  if (role !== 'system') return false;
  const content = toMessageContentString(message.content).trim();
  if (!content) return false;
  return LEGACY_GENERATION_NOISE_PATTERNS.some((pattern) => pattern.test(content));
};

const mergeMessageWithOverlay = (base: Message, overlay: Message): Message => {
  const baseTimelineEventId = getMessageTimelineEventId(base);
  const overlayTimelineEventId = getMessageTimelineEventId(overlay);
  const merged: Message = {
    ...base,
    ...overlay,
  };

  if (baseTimelineEventId) {
    merged.timelineEventId = baseTimelineEventId;
  } else if (overlayTimelineEventId) {
    merged.timelineEventId = overlayTimelineEventId;
  }

  return merged;
};

const readPendingMessages = (
  session: ChatSessionInventoryEntityState | null
): Message[] => {
  if (!session) return [];

  const baseMessages = Array.isArray(session.pendingMessages)
    ? session.pendingMessages
    : Array.isArray(session.runtime?.pendingMessages)
      ? session.runtime.pendingMessages
      : [];

  return baseMessages.filter((message) => {
    if (!message) return false;
    const timelineEventId = getMessageTimelineEventId(message);
    if (!timelineEventId && normalizeNonEmptyString(message.role)?.toLowerCase() === 'system') {
      return false;
    }
    if (isLegacyGenerationNoiseMessage(message)) {
      return false;
    }
    return true;
  });
};

const toSortableTimestamp = (value: string | null | undefined): number => {
  if (typeof value !== 'string') return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toTimelineMessageEventSnapshot = (event: TimelineEventDTO): TimelineMessageEventSnapshot => ({
  id: event.id,
  category: event.category,
  eventType: event.eventType,
  payload: event.payload,
  createdAt: event.createdAt,
  targetEntityType: event.targetEntityType,
  targetEntityId: event.targetEntityId,
  chatSessionId: event.chatSessionId,
});

// Built in a useMemo keyed on the registry reference, NOT serialized inside a store
// selector. The previous form JSON.stringify'd the whole transcript on every store write
// (of any slice) to fake structural equality, which dominated render cost in long sessions.
const buildTimelineEventSnapshots = (
  timelineIds: string[],
  timelineRegistry: Record<string, TimelineEventDTO>
): TimelineMessageEventSnapshot[] => {
  const snapshots: TimelineMessageEventSnapshot[] = [];
  timelineIds.forEach((timelineEventId) => {
    const event = timelineRegistry[timelineEventId];
    if (event && normalizeNonEmptyString(event.id)) {
      snapshots.push(toTimelineMessageEventSnapshot(event));
    }
  });
  return snapshots.length > 0 ? snapshots : EMPTY_TIMELINE_EVENT_SNAPSHOTS;
};

// Only spoken messages render: mutations (generation spawned, layers merged, cut validated) and agent
// bookkeeping stay in the audit timeline, the agent's words and the command trace already saying them.
const timelineEventToMessage = (event: TimelineMessageEventSnapshot): Message | null => {
  // The desk's moves (lib/chat/attachments.ts) draw as a command trace of their own, between the messages they came with.
  if (event.category === 'AGENTIC' && event.eventType === ATTACHMENTS_EVENT_TYPE) {
    const moves = readDeskMoves(event.payload);
    return moves.length ? {
      role: 'assistant', content: '', timestamp: event.createdAt, timelineEventId: event.id,
      scriptsInstances: [{
        id: `attachments-${event.id}`, label: 'Attachments', autorun: false, isAgentGenerated: false,
        commands: moves.map((move, at) => ({ id: `${event.id}-${at}`, action_type: ATTACHMENTS, parameters: {}, status: 'success' as const, description: describeDeskMove(move) })),
      }],
    } : null;
  }
  if (event.category !== 'COMMUNICATION') return null;
  const parsed = parseTimelineMessagePayload(event.payload);
  if (!parsed) return null;
  if (isLegacyGenerationNoiseMessage(parsed as Message)) return null;
  return {
    ...parsed,
    timelineEventId: event.id,
  } as Message;
};

const listTimelineMessagesForFeed = (
  timelineEvents: TimelineMessageEventSnapshot[],
): Message[] => {
  return timelineEvents
    .filter((event) => event.category !== 'TELEMETRY')
    .sort((left, right) => {
      const leftTs = toSortableTimestamp(left.createdAt);
      const rightTs = toSortableTimestamp(right.createdAt);
      if (leftTs === rightTs) return left.id.localeCompare(right.id);
      return leftTs - rightTs;
    })
    .map(timelineEventToMessage)
    .filter((message): message is Message => Boolean(message));
};

const mergeTimelineAndPendingMessages = (
  timelineMessages: Message[],
  pendingMessages: Message[]
): Message[] => {
  const mergedMessages = [...timelineMessages];
  const indexByTimelineEventId = new Map<string, number>();
  const indexByFallbackKey = new Map<string, number>();

  mergedMessages.forEach((message, index) => {
    const timelineEventId = getMessageTimelineEventId(message);
    if (timelineEventId) {
      indexByTimelineEventId.set(timelineEventId, index);
    }
    indexByFallbackKey.set(getMessageFallbackKey(message), index);
  });

  pendingMessages.forEach((message) => {
    const timelineEventId = getMessageTimelineEventId(message);
    const fallbackKey = getMessageFallbackKey(message);

    const existingIndexByTimelineEventId =
      timelineEventId && indexByTimelineEventId.has(timelineEventId)
        ? indexByTimelineEventId.get(timelineEventId)
        : undefined;
    const existingIndexByFallback =
      indexByFallbackKey.has(fallbackKey)
        ? indexByFallbackKey.get(fallbackKey)
        : undefined;
    const existingIndex =
      typeof existingIndexByTimelineEventId === 'number'
        ? existingIndexByTimelineEventId
        : existingIndexByFallback;

    if (typeof existingIndex === 'number' && mergedMessages[existingIndex]) {
      const merged = mergeMessageWithOverlay(mergedMessages[existingIndex]!, message);
      mergedMessages[existingIndex] = merged;
      const mergedTimelineEventId = getMessageTimelineEventId(merged);
      if (mergedTimelineEventId) {
        indexByTimelineEventId.set(mergedTimelineEventId, existingIndex);
      }
      indexByFallbackKey.set(getMessageFallbackKey(merged), existingIndex);
      return;
    }

    const nextIndex = mergedMessages.length;
    mergedMessages.push(message);
    if (timelineEventId) {
      indexByTimelineEventId.set(timelineEventId, nextIndex);
    }
    indexByFallbackKey.set(fallbackKey, nextIndex);
  });

  mergedMessages.sort(
    (left, right) =>
      toSortableTimestamp(left.timestamp) - toSortableTimestamp(right.timestamp)
  );
  return mergedMessages;
};

type TimelineFeedScope = ChatTimelineViewMode;

interface UseSessionMessagesOptions {
  scope?: TimelineFeedScope;
  subject?: ActiveEntityFocus | null;
}

const resolveSubjectKeyFromFocus = (subject: ActiveEntityFocus | null | undefined): string | null => {
  const normalizedSubject = subject ?? null;
  if (!isEntityFocusSubject(normalizedSubject)) return null;
  const entityType = normalizeNonEmptyString(normalizedSubject.entityType);
  const entityId = normalizeNonEmptyString(normalizedSubject.entityId);
  if (!entityType || !entityId) return null;
  return buildTimelineSubjectKey(entityType, entityId);
};

const resolveFeedScope = (
  requestedScope: TimelineFeedScope,
  subjectKey: string | null
): TimelineFeedScope => {
  if (requestedScope === 'subject' && subjectKey) return 'subject';
  return 'session';
};

export const useSessionMessages = (
  sessionId: string | null | undefined,
  options?: UseSessionMessagesOptions
): Message[] => {
  const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
  const requestedScope: TimelineFeedScope = options?.scope === 'subject' ? 'subject' : 'session';
  const subjectKey = useMemo(
    () => resolveSubjectKeyFromFocus(options?.subject),
    [options?.subject?.entityType, options?.subject?.entityId]
  );
  const feedScope = useMemo(
    () => resolveFeedScope(requestedScope, subjectKey),
    [requestedScope, subjectKey]
  );
  const session = useKernelStore((state) =>
    normalizedSessionId
      ? state.inventory.ChatSession.registry[normalizedSessionId] ?? null
      : null
  );
  const timelineIds = useKernelStore((state) =>
    normalizedSessionId
      ? feedScope === 'subject' && subjectKey
        ? (state.timelineFeedIndex.bySubjectKey[subjectKey] ?? EMPTY_TIMELINE_EVENT_IDS)
        : (state.timelineFeedIndex.bySessionId[normalizedSessionId] ?? EMPTY_TIMELINE_EVENT_IDS)
      : EMPTY_TIMELINE_EVENT_IDS
  );
  // Direct Store Access: each selector returns a stable registry reference, so no work
  // runs per store write; the derivations below recompute only when the entities they
  // actually read have changed.
  const timelineRegistry = useKernelStore((state) => state.inventory.TimelineEvent.registry);
  const timelineEvents = useMemo(
    () => buildTimelineEventSnapshots(timelineIds, timelineRegistry),
    [timelineIds, timelineRegistry]
  );

  return useMemo(() => {
    if (!normalizedSessionId || !session) return [];
    const timelineMessages = listTimelineMessagesForFeed(timelineEvents);
    if (feedScope === 'subject') {
      return timelineMessages;
    }
    const pendingMessages = readPendingMessages(session);
    return mergeTimelineAndPendingMessages(timelineMessages, pendingMessages);
  }, [normalizedSessionId, session, feedScope, timelineEvents]);
};

interface UseTimelineFeedMessagesResult {
  messages: Message[];
  scope: TimelineFeedScope;
  hasMoreOlder: boolean;
  isLoadingOlder: boolean;
  loadOlder: () => Promise<void>;
}

export const useTimelineFeedMessages = (
  sessionId: string | null | undefined,
  options?: UseSessionMessagesOptions
): UseTimelineFeedMessagesResult => {
  const { kernel } = useKernel();
  const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
  const requestedScope: TimelineFeedScope = options?.scope === 'subject' ? 'subject' : 'session';
  const subjectKey = useMemo(
    () => resolveSubjectKeyFromFocus(options?.subject),
    [options?.subject?.entityType, options?.subject?.entityId]
  );
  const feedScope = useMemo(
    () => resolveFeedScope(requestedScope, subjectKey),
    [requestedScope, subjectKey]
  );
  const messages = useSessionMessages(normalizedSessionId, {
    scope: feedScope,
    subject: options?.subject ?? null,
  });
  const feedKey = useMemo(() => {
    if (!normalizedSessionId) return null;
    if (feedScope === 'subject' && subjectKey) {
      return buildTimelineSubjectFeedKey(subjectKey);
    }
    return buildTimelineSessionFeedKey(normalizedSessionId);
  }, [normalizedSessionId, feedScope, subjectKey]);
  const feedState = useKernelStore((state) =>
    feedKey ? state.timelineFeedState[feedKey] ?? null : null
  );

  // Only advertise "more older" when there is actually a cursor to load from. A stale
  // hasMoreOlder=true with no cursor would show a "Load older events" button that does
  // nothing (loadOlder bails without a cursor), which is exactly the "nothing to load" bug.
  const hasMoreOlder =
    feedState?.hasMoreOlder === true && !!normalizeNonEmptyString(feedState?.olderCursor);
  const isLoadingOlder = feedState?.isLoading === true;

  const loadOlder = useCallback(async () => {
    if (!kernel.current || !normalizedSessionId) return;
    await kernel.current.loadOlderTimelineEvents({
      sessionId: normalizedSessionId,
      scope: feedScope,
      subject: options?.subject ?? null,
    });
  }, [kernel, normalizedSessionId, feedScope, options?.subject]);

  return {
    messages,
    scope: feedScope,
    hasMoreOlder,
    isLoadingOlder,
    loadOlder,
  };
};
